import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import config from '../config';
import { notifyRbacUpdated } from './rbac';
import { getBrand } from './brand';

const RESTAURANT_PROPERTY = 'R';
// Domain decides the login logo: restaurant host → DineMaster (222.png),
// hotel host → Hotel Desk (LOGO 12 / hoteldesk.png).
const BRAND = getBrand();

/** Matches account/init: propertType, PropertyType, propertyType */
const normalizePropertyType = (data) => {
  const v =
    data?.propertType ?? data?.PropertyType ?? data?.propertyType ?? '';
  return String(v).trim().toUpperCase();
};

const isRestaurantProperty = (data) =>
  normalizePropertyType(data) === RESTAURANT_PROPERTY;

/** So /setup → HotelSetupWizard → RestaurantSetupWizard (isRestaurantCompany). */
const withRestaurantCompanyDetails = (data) => {
  const details = { ...data };
  const pt = normalizePropertyType(data);
  if (pt) details.propertType = pt;
  if (isRestaurantProperty(data)) {
    details.isRestaurant = true;
  }
  return details;
};

function extractHotels(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  for (const key of ['data', 'hotels', 'result', 'results', 'items', 'value']) {
    if (Array.isArray(raw[key])) return raw[key];
  }
  if (raw.id || raw.Id || raw.hotelId || raw.HotelId) return [raw];
  return [];
}

// ── Login errors the server cannot see ──────────────────────────────────────
//
// The API logs every login it refuses (L01-L05, L07, L08). What it can NOT log
// is a request that never reached it - no internet, a network or VPN that
// blocks us, a server too slow to answer. Those are worked out here, shown to
// the person in plain words with a code, and KEPT ON THE PHONE until the phone
// reaches the API again, then sent to api/account/loginerror. Same for a
// company code that matched nothing (L02), the company lookup itself failing
// on the server (L09), and the app failing to open after a good login (L06).
//
// Never the password - only the code, what the person saw, what the browser
// itself said, the company code and the username as typed.

const REQUEST_TIMEOUT_MS = 30000;
const LOGIN_ERROR_QUEUE_KEY = 'pendingLoginErrors';

const NETWORK_MESSAGES = {
  'L00-OFFLINE':
    'No internet connection. Please check your internet and try again.',
  'L00-BLOCKED':
    'Unable to connect to the server. Please try another network, or turn off any VPN / ad-blocker, and try again.',
  'L00-TIMEOUT':
    'The server is taking too long to respond. Please check your internet and try again.',
};

// A fetch that gives up after REQUEST_TIMEOUT_MS, so a request hanging on a
// weak network ends in a clear "too slow" instead of a spinner forever.
async function fetchWithTimeout(url, options = {}, ms = REQUEST_TIMEOUT_MS) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...options, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

// Why a request never got an answer. The browser does not say why a fetch
// failed, so this goes by what it can know: the phone's own online flag, and
// whether our timeout is what stopped it. Anything else - internet on, no
// answer - is a network, VPN, DNS or ad-blocker standing in the way.
const networkCodeFor = (err) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false)
    return 'L00-OFFLINE';
  if (err?.name === 'AbortError') return 'L00-TIMEOUT';
  return 'L00-BLOCKED';
};

// Local clock time without a zone, which is how the server stores times.
const localStamp = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 19);
};

// Storage can throw (private mode, blocked site data) - a report that cannot
// be kept is lost quietly rather than breaking the login screen.
const readLoginErrorQueue = () => {
  try {
    const v = JSON.parse(localStorage.getItem(LOGIN_ERROR_QUEUE_KEY) || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

const writeLoginErrorQueue = (list) => {
  try {
    if (list.length)
      localStorage.setItem(LOGIN_ERROR_QUEUE_KEY, JSON.stringify(list.slice(-20)));
    else localStorage.removeItem(LOGIN_ERROR_QUEUE_KEY);
  } catch {
    /* nowhere to keep it */
  }
};

let flushingLoginErrors = false;

// Sends whatever is waiting. Called whenever this page has just heard from the
// API - that is the proof the phone can reach it now.
async function flushLoginErrors() {
  if (flushingLoginErrors) return;
  const items = readLoginErrorQueue();
  if (!items.length) return;
  flushingLoginErrors = true;
  try {
    const r = await fetchWithTimeout(
      `${config.API_BASE_URL}/account/loginerror`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      },
      15000,
    );
    // Only what was sent comes off - anything queued meanwhile stays.
    if (r.ok) writeLoginErrorQueue(readLoginErrorQueue().slice(items.length));
  } catch {
    /* still cannot reach us - it waits for the next time */
  } finally {
    flushingLoginErrors = false;
  }
}

// Keep it, then try to send it straight away.
function reportLoginError(item) {
  writeLoginErrorQueue([
    ...readLoginErrorQueue(),
    { ...item, occurredAt: localStamp() },
  ]);
  flushLoginErrors();
}

export default function Login() {
  const navigate = useNavigate();

  const [form, setForm] = useState({ username: '', password: '' });
  // Per-field validation messages: { companyName, username, password }. Shown
  // under the field itself rather than in an alert, so the operator can see
  // WHICH box is empty instead of reading a sentence about all three.
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [company, setCompany] = useState(null);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [companyName, setCompanyName] = useState('');
  // ── Email-OTP password reset ──
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1 = send OTP, 2 = verify
  const [resetUsername, setResetUsername] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPass, setResetNewPass] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetMaskedEmail, setResetMaskedEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  // Latest loaded company + the in-flight init() promise. The init API is
  // triggered when the operator LEAVES the company-code field (onBlur), not on
  // every keystroke — keystroke-firing raced the Login click and showed a false
  // "Company not loaded". These refs let handleLogin await whatever load is
  // already running instead of re-firing init on click.
  const companyRef = useRef(null);
  const loadInFlight = useRef(null);
  // Why the last company lookup came back empty, when it was NOT simply a code
  // that matched nothing: { code, detail }. Without it no internet read as
  // "This company code is not correct" and sent people checking a code that
  // was right all along.
  const initFailure = useRef(null);
  // Reload logic
  useEffect(() => {
    const hasReloaded = sessionStorage.getItem('loginReloaded');
    if (!hasReloaded) {
      sessionStorage.setItem('loginReloaded', 'true');
      window.location.reload();
    }
  }, []);

  // Load Company Info
  useEffect(() => {
    loadCompany();
    // A failure kept on this phone from an earlier visit goes as soon as the
    // page opens - if the API is reachable now.
    flushLoginErrors();
  }, []);
  // init(): resolve the company from the typed code. Returns the loaded company
  // (or null on failure) and records the promise so a Login click can await it.
  const loadCompany = (clientValue) => {
    const client = (clientValue || '').trim();
    if (!client) return Promise.resolve(null);

    const promise = (async () => {
      initFailure.current = null;
      try {
        // SAVE CLIENT HERE
        localStorage.setItem('client', client);

        console.log('🔥 Final Client Used:', client);

        let res;
        try {
          res = await fetchWithTimeout(
            `${config.API_BASE_URL}/account/init?client=${encodeURIComponent(client)}`,
            { method: 'GET' },
          );
        } catch (netErr) {
          initFailure.current = {
            code: networkCodeFor(netErr),
            detail: `Company lookup: ${netErr?.name || 'Error'}: ${netErr?.message || netErr}`,
          };
          return null;
        }

        // The phone has just reached the API - anything waiting goes now.
        flushLoginErrors();

        if (!res.ok) {
          // The lookup broke on the server. That is not a wrong code.
          initFailure.current = {
            code: 'L09',
            detail: `Company lookup answered HTTP ${res.status}`,
          };
          return null;
        }

        const data = await res.json();
        const companyDetails = withRestaurantCompanyDetails(data);
        const propertyType = normalizePropertyType(data);

        console.log('✅ Company Loaded:', companyDetails);

        setCompany(companyDetails);
        companyRef.current = companyDetails;

        sessionStorage.setItem('companyId', companyDetails.id);
        if (propertyType) {
          sessionStorage.setItem('propertType', propertyType);
        }
        sessionStorage.setItem('companyDetails', JSON.stringify(companyDetails));
        return companyDetails;
      } catch (err) {
        console.error('❌ Init Error:', err);
        return null;
      }
    })();

    loadInFlight.current = promise;
    return promise;
  };

  // Guarantees a loaded company without re-firing init on click: reuse the
  // already-loaded one, else await the in-flight blur-triggered load, else (only
  // if the operator never left the field) do a single final load.
  const ensureCompany = async () => {
    if (companyRef.current?.id) return companyRef.current;
    if (loadInFlight.current) await loadInFlight.current;
    if (companyRef.current?.id) return companyRef.current;
    return loadCompany(companyName);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // The message goes the moment the operator starts fixing the field.
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const handleLogin = async () => {
    // Every empty box is named, all three at once - telling the operator about
    // one, then the next, is three round trips for one form.
    const nextErrors = {};
    if (!companyName.trim()) nextErrors.companyName = 'Please fill company code';
    if (!form.username.trim()) nextErrors.username = 'Please fill username';
    if (!form.password) nextErrors.password = 'Please fill password';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const comp = await ensureCompany();
    if (!comp?.id) {
      const typedCode = companyName.trim();
      const typedUser = form.username.trim();
      const failure = initFailure.current;

      // Could not reach the server at all - the code may be perfectly right.
      if (failure && NETWORK_MESSAGES[failure.code]) {
        const message = NETWORK_MESSAGES[failure.code];
        alert(`${message} (Code: ${failure.code})`);
        reportLoginError({
          errorCode: failure.code,
          companyCode: typedCode,
          username: typedUser,
          message,
          detail: failure.detail,
        });
        return;
      }

      // Reached the server, and the lookup itself broke there.
      if (failure?.code === 'L09') {
        const message =
          'We could not load your company right now. Please try again in a few minutes.';
        alert(`${message} (Code: L09)`);
        reportLoginError({
          errorCode: 'L09',
          companyCode: typedCode,
          username: typedUser,
          message,
          detail: failure.detail,
        });
        return;
      }

      // The server answered and no company matched: a WRONG code, not a page
      // that needs refreshing, and saying so sends people to the right fix.
      const message = 'Company code is not valid. Please check it.';
      setErrors({ companyName: `${message} (Code: L02)` });
      reportLoginError({
        errorCode: 'L02',
        companyCode: typedCode,
        username: typedUser,
        message,
      });
      return;
    }

    setLoading(true);

    try {
      // The request on its own, so "the server never answered" can be told
      // apart from everything else. Folded into the catch below, a phone with
      // no route to the API and a bug after a successful login both came out
      // as the same "Server error. Please try again later." - and a client's
      // screenshot of it told us nothing.
      let response;
      try {
        response = await fetchWithTimeout(`${config.API_BASE_URL}/account/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: form.username,
            password: form.password,
            companyid: comp.id,
          }),
        });
      } catch (netErr) {
        console.error('❌ Login request did not reach the server:', netErr);
        // The server never saw this, so it cannot log it. Kept on the phone
        // and sent the next time the phone reaches the API.
        const code = networkCodeFor(netErr);
        const message = NETWORK_MESSAGES[code];
        alert(`${message} (Code: ${code})`);
        reportLoginError({
          errorCode: code,
          companyId: comp.id,
          companyCode: companyName.trim(),
          username: form.username.trim(),
          message,
          detail: `Login request: ${netErr?.name || 'Error'}: ${netErr?.message || netErr}`,
        });
        setLoading(false);
        return;
      }

      // An answer came back, so the phone can reach the API - send anything
      // that was waiting from an earlier failure.
      flushLoginErrors();

      if (!response.ok) {
        // The API's own words and code (L01-L08, see AccountController), so a
        // screenshot says which refusal it was. The API has already logged
        // it. Written to be safe to show: no technical detail.
        // The fallbacks only cover an API that has not been redeployed yet.
        const err = await response.json().catch(() => null);
        const message =
          err?.message ||
          (err?.accountDeleted
            ? 'Your account is deleted.'
            : response.status >= 500
              ? 'We could not sign you in right now. Please try again in a few minutes.'
              : 'Wrong username or password.');
        const code = err?.errorCode || `HTTP ${response.status}`;
        const ref = err?.reference ? `, Ref: ${err.reference}` : '';
        alert(`${message} (Code: ${code}${ref})`);
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.message === 'Login successful') {
        sessionStorage.setItem('user', JSON.stringify(data.user));
        sessionStorage.setItem('username', data.user.username);
        sessionStorage.setItem('userId', data.user.id);
        sessionStorage.setItem('token', data.token);
        sessionStorage.setItem('permissions', JSON.stringify(data.permisioon));
        const rbacCodes = data.permissionCodes ?? data.PermissionCodes;
        if (Array.isArray(rbacCodes) && rbacCodes.length > 0) {
          sessionStorage.setItem('permissionCodes', JSON.stringify(rbacCodes));
        } else {
          sessionStorage.removeItem('permissionCodes');
        }
        notifyRbacUpdated();

        // One-time direct-login URL returned by the login API (single-use link).
        if (data.directLoginUrl) {
          sessionStorage.setItem('directLoginUrl', data.directLoginUrl);
        } else {
          sessionStorage.removeItem('directLoginUrl');
        }

        console.log('✅ Login Success:', data);

        // Ask the dashboard to hard-refresh ONCE on this login (it consumes
        // this flag and reloads a single time). Set per-login so every fresh
        // login gets exactly one refresh — never a loop.
        sessionStorage.setItem('pendingDashboardRefresh', '1');

        // ── First-time setup check ──────────────────────────
        // Hotel (default): no properties → HotelSetupWizard at /setup.
        // Restaurant (propertType R from account/init): incomplete → same route;
        // HotelSetupWizard delegates to RestaurantSetupWizard when isRestaurant is set.
        const isRestaurant = isRestaurantProperty(comp);
        try {
          const hotelRes = await fetch(
            `${config.API_BASE_URL}/Home/GetHotels/${encodeURIComponent(comp.id)}`,
            { headers: { Authorization: `Bearer ${data.token}` } },
          );
          if (hotelRes.ok) {
            const raw = await hotelRes.json();
            const hotels = extractHotels(raw);
            const hasProperties = hotels.length > 0;
            if (!hasProperties) {
              localStorage.removeItem('hotelSetupDone');
              navigate('/setup');
              return;
            }
          } else if (isRestaurant) {
            localStorage.removeItem('hotelSetupDone');
            navigate('/setup');
            return;
          }
        } catch (e) {
          if (isRestaurant) {
            console.warn(
              'Restaurant setup check failed, opening setup wizard:',
              e,
            );
            localStorage.removeItem('hotelSetupDone');
            navigate('/setup');
            return;
          }
          console.warn('Hotel check failed, proceeding to dashboard:', e);
        }
        // ────────────────────────────────────────────────────

        // ── Subscription access-check (additive; fails open) ──
        // A company can use HMS only if a current subscription exists and is
        // not expired. On any error we proceed normally so login is never
        // blocked by the check itself.
        try {
          const subRes = await fetch(
            `${config.API_BASE_URL}/Subscription/status/${encodeURIComponent(comp.id)}`,
            { headers: { Authorization: `Bearer ${data.token}` } },
          );
          if (subRes.ok) {
            const sub = await subRes.json();
            sessionStorage.setItem('subscriptionStatus', JSON.stringify(sub));
            if (sub && sub.canAccess === false) {
              // Expired / none → send them to renew instead of the dashboard.
              localStorage.setItem('hotelSetupDone', 'true');
              navigate('/admin/Subscription');
              return;
            }
          }
        } catch (e) {
          console.warn('Subscription check failed, proceeding to dashboard:', e);
        }

        localStorage.setItem('hotelSetupDone', 'true');
        navigate('/admin');
      } else {
        alert('Wrong username or password');
      }
    } catch (error) {
      // Only reached AFTER the server answered: the login response could not be
      // read, or something failed while opening the app. L06, so it is never
      // mistaken for L00 (no answer at all). The browser's own error goes to
      // the log - never onto the screen.
      console.error('❌ Login Error:', error);
      const message =
        'Signed in, but the app could not open. Please refresh the page and try again.';
      alert(`${message} (Code: L06)`);
      reportLoginError({
        errorCode: 'L06',
        companyId: comp?.id,
        companyCode: companyName.trim(),
        username: form.username.trim(),
        message,
        detail: `${error?.name || 'Error'}: ${error?.message || error}\n${String(
          error?.stack || '',
        ).slice(0, 1500)}`,
      });
    }

    setLoading(false);
  };

  // Forgot Password Handler
  const handleForgotPassword = async () => {
    if (!form.username) {
      alert('Please enter your username first');
      return;
    }
    const comp = await ensureCompany();
    if (!comp?.id) {
      alert('Company not loaded. Please refresh.');
      return;
    }

    setForgotLoading(true);

    try {
      const response = await fetch(
        `${config.API_BASE_URL}/account/forgotpassword`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: form.username,
            companyid: comp.id,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        alert(
          data.message ||
            'Password reset link has been sent to your registered email.',
        );
      } else {
        alert(data.message || 'Failed to process forgot password request.');
      }
    } catch (error) {
      console.error('❌ Forgot Password Error:', error);
      alert('Something went wrong. Please try again later.');
    }

    setForgotLoading(false);
  };

  // ── Email-OTP password reset handlers ──
  const openReset = () => {
    setResetUsername(form.username || '');
    setResetStep(1);
    setResetOtp('');
    setResetNewPass('');
    setResetMsg('');
    setResetMaskedEmail('');
    setResetOpen(true);
  };

  const sendResetOtp = async () => {
    if (!resetUsername.trim()) {
      setResetMsg('Please enter your username.');
      return;
    }
    const comp = await ensureCompany();
    if (!comp?.id) {
      setResetMsg('Company not loaded. Please refresh.');
      return;
    }
    setResetLoading(true);
    setResetMsg('');
    try {
      const res = await fetch(`${config.API_BASE_URL}/passwordreset/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: resetUsername.trim(),
          companyId: comp.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResetMsg(data.error || 'Failed to send OTP.');
        return;
      }
      setResetMaskedEmail(data.email || '');
      setResetStep(2);
      setResetMsg('OTP sent to your registered email.');
    } catch {
      setResetMsg('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const submitReset = async () => {
    if (!resetOtp.trim() || !resetNewPass) {
      setResetMsg('Enter the OTP and a new password.');
      return;
    }
    const comp = await ensureCompany();
    if (!comp?.id) {
      setResetMsg('Company not loaded. Please refresh.');
      return;
    }
    setResetLoading(true);
    setResetMsg('');
    try {
      const res = await fetch(`${config.API_BASE_URL}/passwordreset/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: resetUsername.trim(),
          companyId: comp.id,
          otp: resetOtp.trim(),
          newPassword: resetNewPass,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResetMsg(data.error || 'Failed to reset password.');
        return;
      }
      alert('Password reset successful. Please log in with your new password.');
      setResetOpen(false);
    } catch {
      setResetMsg('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1A56DB 100%)',
        padding: '20px 10px',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}>
      <div
        style={{
          maxWidth: 440,
          width: '100%',
          padding: '40px 30px',
          borderRadius: 20,
          boxShadow: '0 15px 50px rgba(0,0,0,0.3)',
          backgroundColor: '#fff',
        }}>
        {/* Logo & Powered By */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component='img'
            src={BRAND.logo}
            alt={BRAND.name}
            sx={{
              maxHeight: 95,
              width: 'auto',
              objectFit: 'contain',
              mb: 2,
            }}
          />
          <Typography sx={{ fontSize: 13.5, color: '#666', fontWeight: 500 }}>
            Powered By :- Go-ERP.Live
          </Typography>
        </Box>

        <h2
          style={{
            textAlign: 'center',
            marginBottom: 35,
            color: '#1A56DB',
            fontWeight: 700,
            fontSize: '28px',
          }}>
          Welcome Back
        </h2>
        {/* Company Name */}
        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
              color: '#444',
            }}>
            Company Code
          </label>

          <input
            type='text'
            value={companyName}
            onChange={(e) => {
              setCompanyName(e.target.value);
              setErrors((prev) => ({ ...prev, companyName: '' }));
            }}
            placeholder='Enter company Code'
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: 10,
              border: `1.5px solid ${errors.companyName ? '#d32f2f' : '#ddd'}`,
              fontSize: 16,
              outline: 'none',
              transition: 'all 0.2s',
            }}
            onFocus={(e) =>
              (e.target.style.borderColor = errors.companyName ? '#d32f2f' : '#1A56DB')
            }
            onBlur={(e) => {
              e.target.style.borderColor = errors.companyName ? '#d32f2f' : '#ddd';
              // Run init() when the operator leaves the field (e.g. tabs to
              // Username or clicks Login) — once, with the full code.
              loadCompany(companyName);
            }}
          />
          {errors.companyName && (
            <div style={{ color: '#d32f2f', fontSize: 13, marginTop: 6 }}>
              {errors.companyName}
            </div>
          )}
        </div>
        {/* Username */}
        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
              color: '#444',
            }}>
            Username
          </label>
          <input
            type='text'
            name='username'
            value={form.username}
            onChange={handleChange}
            placeholder='Enter your username'
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: 10,
              border: `1.5px solid ${errors.username ? '#d32f2f' : '#ddd'}`,
              fontSize: 16,
              outline: 'none',
              transition: 'all 0.2s',
            }}
            onFocus={(e) =>
              (e.target.style.borderColor = errors.username ? '#d32f2f' : '#1A56DB')
            }
            onBlur={(e) =>
              (e.target.style.borderColor = errors.username ? '#d32f2f' : '#ddd')
            }
          />
          {errors.username && (
            <div style={{ color: '#d32f2f', fontSize: 13, marginTop: 6 }}>
              {errors.username}
            </div>
          )}
        </div>

        {/* Password with Show/Hide */}
        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
              color: '#444',
            }}>
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              name='password'
              value={form.password}
              onChange={handleChange}
              placeholder='Enter your password'
              style={{
                width: '100%',
                padding: '14px 50px 14px 16px',
                borderRadius: 10,
                border: `1.5px solid ${errors.password ? '#d32f2f' : '#ddd'}`,
                fontSize: 16,
                outline: 'none',
                transition: 'all 0.2s',
              }}
              onFocus={(e) =>
                (e.target.style.borderColor = errors.password ? '#d32f2f' : '#1A56DB')
              }
              onBlur={(e) =>
                (e.target.style.borderColor = errors.password ? '#d32f2f' : '#ddd')
              }
            />
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
              }}>
              {showPassword ? (
                <span style={{ fontSize: 20 }}>🙈</span>
              ) : (
                <span style={{ fontSize: 20 }}>👁️</span>
              )}
            </button>
          </div>
          {errors.password && (
            <div style={{ color: '#d32f2f', fontSize: 13, marginTop: 6 }}>
              {errors.password}
            </div>
          )}
        </div>

        {/* Forgot Password Button — opens email-OTP reset */}
        <div style={{ textAlign: 'right', marginBottom: 30 }}>
          <button
            type='button'
            onClick={openReset}
            style={{
              background: 'none',
              border: 'none',
              color: '#1A56DB',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}>
            Forgot Password?
          </button>
        </div>

        {/* Login Button */}
        <button
          onClick={handleLogin}
          disabled={loading}
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: 10,
            backgroundColor: loading ? '#93c5fd' : '#1A56DB',
            border: 'none',
            color: 'white',
            fontSize: 17,
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.3s ease',
            marginBottom: 20,
          }}>
          {loading ? 'Logging in...' : 'Login'}
        </button>

        <Typography
          variant='body2'
          sx={{
            textAlign: 'center',
            color: '#555',
            fontSize: 14,
            mb: 1.5,
          }}>
          Don&apos;t have an account?{' '}
          <a
            href='https://signup.goerp.live/Account/Signup'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#1A56DB', fontWeight: 700, textDecoration: 'none' }}>
            Sign Up
          </a>
        </Typography>

        <Typography
          variant='body2'
          sx={{
            textAlign: 'center',
            color: '#777',
            fontSize: 13.5,
          }}>
          Need help? Contact your Administrator
        </Typography>
      </div>

      {/* ── Email-OTP password reset modal ── */}
      {resetOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}>
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              width: '100%',
              maxWidth: 380,
              padding: '26px 24px',
              boxShadow: '0 15px 50px rgba(0,0,0,0.3)',
            }}>
            <h3 style={{ margin: 0, marginBottom: 6, color: '#1A56DB' }}>
              Reset Password
            </h3>
            <p style={{ margin: 0, marginBottom: 16, fontSize: 13, color: '#666' }}>
              {resetStep === 1
                ? 'Enter your username. An OTP will be sent to your registered email.'
                : `Enter the OTP${resetMaskedEmail ? ` sent to ${resetMaskedEmail}` : ''} and your new password.`}
            </p>

            {resetStep === 1 ? (
              <input
                value={resetUsername}
                onChange={(e) => setResetUsername(e.target.value)}
                placeholder='Username'
                style={{
                  width: '100%',
                  padding: '13px 14px',
                  borderRadius: 10,
                  border: '1.5px solid #ddd',
                  fontSize: 15,
                  outline: 'none',
                }}
              />
            ) : (
              <>
                <input
                  value={resetOtp}
                  onChange={(e) => setResetOtp(e.target.value)}
                  placeholder='6-digit OTP'
                  style={{
                    width: '100%',
                    padding: '13px 14px',
                    borderRadius: 10,
                    border: '1.5px solid #ddd',
                    fontSize: 15,
                    outline: 'none',
                  }}
                />
                <input
                  type='password'
                  value={resetNewPass}
                  onChange={(e) => setResetNewPass(e.target.value)}
                  placeholder='New password'
                  style={{
                    width: '100%',
                    padding: '13px 14px',
                    borderRadius: 10,
                    border: '1.5px solid #ddd',
                    fontSize: 15,
                    outline: 'none',
                    marginTop: 10,
                  }}
                />
              </>
            )}

            {resetMsg && (
              <div style={{ fontSize: 12.5, color: '#1A56DB', marginTop: 8 }}>
                {resetMsg}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button
                type='button'
                onClick={() => setResetOpen(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  border: '1.5px solid #ddd',
                  background: '#fff',
                  color: '#444',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}>
                Cancel
              </button>
              <button
                type='button'
                disabled={resetLoading}
                onClick={resetStep === 1 ? sendResetOtp : submitReset}
                style={{
                  flex: 2,
                  padding: '12px',
                  borderRadius: 10,
                  border: 'none',
                  background: resetLoading ? '#93c5fd' : '#1A56DB',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: resetLoading ? 'not-allowed' : 'pointer',
                }}>
                {resetLoading
                  ? 'Please wait...'
                  : resetStep === 1
                    ? 'Send OTP'
                    : 'Reset Password'}
              </button>
            </div>

            {resetStep === 2 && (
              <button
                type='button'
                onClick={sendResetOtp}
                disabled={resetLoading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1A56DB',
                  fontSize: 12.5,
                  marginTop: 12,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}>
                Resend OTP
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
